import axios from 'axios'
import HTTP from '@rocketfid/api'

const state = {
  items: [],
  itemMoving: null,
  copiedBadge: null
}

// getters
const getters = {
  badges: (state) => {
    return state.items
  },
  badgeMoving: (state) => {
    return state.itemMoving
  },
  getByCampaign: (state) => (campaign) => {
    return state.items.filter(item => item.category.id === campaign.id)
  },
  getById: (state) => (id) => {
    let parent = state.items.find(item => item.id === id)
    let child = state.items.find(item => item.children.find(it => it.id === id))

    return parent || child.children.find(it => it.id === id)
  },
  copiedBadge: (state) => {
    return state.copiedBadge
  }
}

// actions
const actions = {
  startMove ({ commit }, badge) {
    commit('SET_BADGE_MOVING', { badge: badge })
  },

  loadBadges ({ commit, dispatch, rootGetters }) {
    let sitekey = rootGetters['account/currentSitekey']

    return HTTP
      .setInstance(sitekey)
      .badge.getAll()
      .then((badges) => {
        dispatch('badges/resetBadges', null, { root: true })
        commit('ADD_BADGE_ITEMS', { items: badges })
        return badges
      })
  },
  resetBadges ({ commit }) {
    return new Promise((resolve) => {
      commit('RESET_BADGE_ITEMS')
      resolve()
    })
  },

  addBadge ({ commit, rootGetters }, { badge }) {
    let sitekey = rootGetters['account/currentSitekey']

    return HTTP
      .setInstance(sitekey)
      .badge.create(badge)
      .then((badge) => {
        commit('ADD_BADGE_ITEM', { item: badge })
        return badge
      })
  },

  updateBadge ({ commit, rootGetters }, { badge }) {
    let sitekey = rootGetters['account/currentSitekey']

    return HTTP
      .setInstance(sitekey)
      .badge.update(badge)
      .then((badge) => {
        commit('UPDATE_BADGE_ITEM', { item: badge })
        return badge
      })
  },
  moveBadge ({ commit, dispatch, rootGetters }, { badge }) {
    let sitekey = rootGetters['account/currentSitekey']

    return HTTP
      .setInstance(sitekey)
      .badge.update(badge)
      .then((badge) => {
        commit('MOVE_BADGE_ITEM', { item: badge })
        dispatch('badges/loadBadges', null, { root: true })
        return badge
      })
  },
  copyBadge ({ commit }, badge) {
    commit('COPY_BADGE_ITEM', { item: badge })
  },
  async pasteBadge ({ commit, rootGetters }, badge) {
    try {
      let sitekey = rootGetters['account/currentSitekey']
      const url = process.env.API + '/achievement/'
      const headers = {headers: { 'X-Instance': sitekey }}
      const { data: newBadge } = await axios.post(url, badge, headers)
      // If badge has children post a badge for each one
      if (badge.children) {
        const children = badge.children
        // Get category and parent id from new badge
        const { id: parentID, category: {id: categoryId} } = newBadge

        for (let i = 0; i <= children.length - 1; i++) {
          children[i].parent = {}
          children[i].parent.id = parentID
          children[i].category = {}
          children[i].category.id = categoryId
          const { data: newBadgeChild } = await axios.post(url, children[i], headers)
          newBadge.children.push(newBadgeChild)
        }
      }

      commit('ADD_BADGE_ITEM', { item: newBadge })
      commit('COPY_BADGE_ITEM', { item: null })
    } catch (err) {
      return err
    }
  },
  removeBadge ({ commit, rootGetters }, { badge }) {
    let sitekey = rootGetters['account/currentSitekey']

    return HTTP
      .setInstance(sitekey)
      .badge.delete(badge)
      .then(() => {
        commit('REMOVE_BADGE_ITEM', { item: badge })
      })
  },

  linkEvent ({ commit, rootGetters }, { badge, event }) {
    let sitekey = rootGetters['account/currentSitekey']

    return new Promise((resolve, reject) => {
      const url = process.env.API + '/achievement/' + badge.id + '/'
      const headers = {headers: { 'X-Instance': sitekey }}

      axios.put(url + 'event/' + event.id + '/', headers)
        .then(response => {
          let badge = response.data
          badge.sitekey = sitekey
          resolve(badge)
        }).then(() => {
          return HTTP
            .setInstance(sitekey)
            .badge.get(badge.id)
            .then((badge) => {
              commit('UPDATE_BADGE_ITEM', { items: badge })
              resolve(badge)
            })
        })
        .catch(e => {
          return HTTP
            .setInstance(sitekey)
            .badge.get(badge.id)
            .then((badge) => {
              commit('UPDATE_BADGE_ITEM', { item: badge })
              reject(e)
            })
        })
    })
  },
  unLinkEvent ({ commit, rootGetters }, { badge, event }) {
    let sitekey = rootGetters['account/currentSitekey']

    return new Promise((resolve, reject) => {
      const url = process.env.API + '/achievement/' + badge.id + '/'
      const headers = {headers: { 'X-Instance': sitekey }}

      axios.delete(url + 'event/' + event.id + '/', headers)
        .then(response => {
          resolve(response)
        }).then(() => {
          return HTTP
            .setInstance(sitekey)
            .badge.get(badge.id)
            .then((badge) => {
              commit('UPDATE_BADGE_ITEM', { item: badge })
              resolve(badge)
            })
        })
        .catch(e => {
          return HTTP
            .setInstance(sitekey)
            .badge.get(badge.id)
            .then((badge) => {
              commit('UPDATE_BADGE_ITEM', { items: badge })
              reject(e)
            })
        })
    })
  }
}

// mutations
const mutations = {
  SET_BADGE_MOVING: (state, { badge }) => {
    state.itemMoving = badge
  },

  ADD_BADGE_ITEMS: (state, { items }) => {
    state.items.push(...items)
  },

  RESET_BADGE_ITEMS: (state) => {
    state.items = []
  },

  ADD_BADGE_ITEM: (state, { item }) => {
    if (item.parent && item.parent.id) {
      // Is child
      for (let i = 0; i < state.items.length; i++) {
        if (state.items[i].id === item.parent.id) {
          // Parent finded
          state.items[i].children.push(item)
        }
      }
    } else {
      // Is parent
      state.items.push(item)
    }
  },

  UPDATE_BADGE_ITEM: (state, { item }) => {
    let parent = state.items.find(bdg => bdg.id === item.id)
    let child = state.items.find(bdg => bdg.children.find(bdgChild => bdgChild.id === item.id))

    let badge = parent || child.children.find(bdgChild => bdgChild.id === item.id)

    return Object.assign(badge, item)
  },
  MOVE_BADGE_ITEM: (state, { item }) => {
    state.items.forEach((badge, badgeIndex) => {
      if (item.id === badge.id) {
        state.items.splice(badgeIndex, 1)
      }

      // Check childreen
      badge.children.forEach((child, childIndex) => {
        if (item.id === child.id) {
          badge.children.splice(childIndex, 1)
        }
      })
    })

    // ADD
    if (item.parent && item.parent.id) {
      // Is child
      for (let i = 0; i < state.items.length; i++) {
        if (state.items[i].id === item.parent.id) {
          // Parent finded
          state.items[i].children.push(item)
        }
      }
    } else {
      // Is parent
      state.items.push(item)
    }
  },
  REMOVE_BADGE_ITEM: (state, { item }) => {
    if (item.parent && item.parent.id) {
      // Is child
      for (let i = 0; i < state.items.length; i++) {
        if (state.items[i].id === item.parent.id) {
          // Parent finded
          for (let d = 0; d < state.items[i].children.length; d++) {
            if (state.items[i].children[d].id === item.id) {
              return state.items[i].children.splice(d, 1)
            }
          }
        }
      }
    } else {
      // Is Parent
      for (let i = 0; i < state.items.length; i++) {
        if (state.items[i].id === item.id) {
          return state.items.splice(i, 1)
        }
      }
    }
  },
  COPY_BADGE_ITEM: (state, { item }) => {
    state.copiedBadge = item
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/badges.js